import { Link } from "react-router-dom";
import { ArrowUpRight, Layers, Users, Grid3x3, ShieldCheck, FileCheck2, Ruler, Lock, Handshake } from "lucide-react";
import { Hero } from "@/components/site/Hero";
import { StatsBand } from "@/components/site/Stats";
import { Reveal, SectionHead, MaskImage } from "@/components/site/Reveal";
import { Btn, ServiceCard } from "@/components/site/Bits";
import { PointToCad, ProcessFlow } from "@/components/site/Visuals";
import { Gallery } from "@/components/site/Gallery";
import { Marquee } from "@/components/site/Marquee";
import { FinalCta } from "@/components/site/FinalCta";
import { Platforms, Process, TrialOffer, Clusters, Software } from "@/components/site/HomeOps";
import { Contours, Tripod, Axes, Section as SectionVec } from "@/components/site/Vectors";
import { HOME_CARDS, getService } from "@/data/services";
import { useSeo, ORG_LD } from "@/lib/seo";
import { useTrack } from "@/lib/track";

const WHY = [
  [Users, "Dedicated Technical Team", "Subject experts, senior and junior technicians working within a structured review chain."],
  [Grid3x3, "Broad Survey Drafting Coverage", "From boundary and ALTA/NSPS to point clouds and GIS, under one partner."],
  [Layers, "Structured CAD Production", "Consistent layers, blocks and sheet setups on every project."],
  [Ruler, "Client-Specific Drawing Standards", "We adopt your templates, symbology and annotation rules."],
  [FileCheck2, "Quality-Focused Review", "Drawings are checked against the supplied data before delivery."],
  [Lock, "Confidential Project Handling", "Your data and drawings are used only for your project."],
  [Handshake, "Outsourced Drafting Support", "Extra drafting capacity when your workload peaks, or every month."],
];
const STEPS = ["Submit your survey data.", "Review project scope and requirements.", "Prepare the CAD drawing.", "Perform quality checks.", "Deliver the agreed files."];
const PRICING = [["Per-Project Pricing", "A"], ["Per-Sheet Pricing", "B"], ["Hourly Technical Support", "C"], ["Monthly Outsourcing Plans", "D"]];

export default function Home() {
  useSeo({ title: "Land Survey CAD Drafting & Geospatial Services | GEO DRAFTING", path: "/", jsonLd: ORG_LD,
    description: "Survey CAD drafting services for land surveyors and engineers: boundary, ALTA/NSPS, topographic, utility, as-built and point cloud to CAD drafting from your field data. USA, Canada, Australia, UK." });
  useTrack("home");
  return (
    <>
      <Hero />
      <section className="relative bg-white" aria-label="Company statistics"><div className="mx-auto max-w-[1400px]"><StatsBand /></div></section>

      {/* What we do */}
      <section className="relative overflow-hidden bg-white py-24 md:py-32" data-testid="what-we-do-section">
        <Tripod className="absolute -left-10 bottom-0 hidden md:block w-56 text-[#1769AA] opacity-[.12]" />
        <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr] lg:items-center">
          <div>
            <SectionHead eyebrow="What we do" title={["Your specialist partner", "in survey CAD drafting."]} />
            <Reveal delay={0.1}><p className="mt-8 text-base md:text-lg leading-relaxed text-[#526579]">GEO DRAFTING provides survey drawing production and technical data processing from client-supplied field measurements, coordinates, notes, sketches and existing survey information.</p></Reveal>
            <Reveal delay={0.15}><p className="mt-4 text-sm leading-relaxed text-[#526579] border-l-2 border-[#D8B66A] pl-4">We are a drafting partner, not a field survey crew. Field measurement, boundary determination and professional certification remain with the licensed surveyor; on-site surveying is only offered where separately confirmed.</p></Reveal>
            <Reveal delay={0.2}><Btn to="/services" className="mt-10" testid="discover-services-button">Discover Our Services</Btn></Reveal>
          </div>
          <Reveal delay={0.1}><PointToCad /></Reveal>
        </div>
      </section>

      <Marquee />

      <Platforms />

      <Clusters />

      {/* All types */}
      <section className="relative bg-[#F3F6F8] grid-light py-24 md:py-32" data-testid="all-types-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionHead eyebrow="Survey drafting catalogue" title={["All types of land survey drafting.", "One technical partner."]} />
            <Reveal><Btn to="/services" variant="outline" testid="view-all-services-button">View All Services</Btn></Reveal>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {HOME_CARDS.map(([title, slug, art], i) => (
              <ServiceCard key={title} title={title} slug={slug} art={art} short={getService(slug)?.short} index={i} code={String(i + 1).padStart(2, "0")} />
            ))}
          </div>
        </div>
      </section>

      {/* How we operate — six stages + free trial */}
      <Process />
      <Software />
      <TrialOffer />

      {/* Field to CAD */}
      <section className="on-dark relative overflow-hidden bg-[#102A43] grid-dark py-24 md:py-32 text-white" data-testid="field-to-cad-section">
        <Axes className="absolute right-0 top-10 w-[420px] text-[#5AA9E6] opacity-[.12]" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHead dark eyebrow="Process" title={["From field to CAD."]} intro="Every drawing follows the same disciplined path: scattered data points become organized linework, and organized linework becomes a checked, complete survey drawing." />
          <div className="mt-16"><ProcessFlow /></div>
        </div>
      </section>

      {/* Why choose */}
      <section className="relative bg-white py-24 md:py-32" data-testid="why-choose-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHead eyebrow="Why GEO DRAFTING" title={["Why choose", "GEO DRAFTING?"]} />
          <div className="mt-14 grid gap-px bg-[#102A43]/12 border border-[#102A43]/12 sm:grid-cols-2 lg:grid-cols-4">
            <Reveal className="relative overflow-hidden bg-[#102A43] p-8 md:p-10 text-white sm:col-span-2 lg:row-span-2 flex flex-col justify-between min-h-[320px]">
              <Contours className="absolute -right-24 -bottom-24 w-[520px] text-[#5AA9E6] opacity-25" />
              <ShieldCheck className="relative h-8 w-8 text-[#D8B66A]" />
              <div className="relative">
                <p className="font-display text-7xl md:text-8xl font-extrabold">10<span className="text-[#D8B66A]">+</span></p>
                <h3 className="mt-2 font-display text-2xl font-bold">Years of Experience</h3>
                <p className="mt-3 max-w-md text-sm text-[#c3d0dd]">A decade of focused work in land surveying CAD drafting and survey data processing.</p>
              </div>
            </Reveal>
            {WHY.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={(i % 4) * 0.06} className={`group bg-white p-7 transition-colors duration-300 hover:bg-[#F3F6F8] ${i === 4 ? "sm:col-span-2 lg:col-span-2" : ""}`}>
                <Icon className="h-6 w-6 text-[#1769AA] transition-transform duration-300 group-hover:-translate-y-1" />
                <h3 className="mt-6 font-display text-lg font-bold text-[#102A43]">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#526579]">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="on-dark relative bg-[#0A1D30] py-24 md:py-32 text-white" data-testid="sample-drawings-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHead dark eyebrow="Sample survey drawings" title={["Drawing previews."]} intro="Illustrative demonstration drawings created by GEO DRAFTING to show typical sheet content. They are not client projects; real portfolio work will only be shown with client permission." />
          <div className="mt-14"><Gallery /></div>
        </div>
      </section>

      {/* Workflow */}
      <section className="relative overflow-hidden bg-white py-24 md:py-32" data-testid="workflow-section">
        <SectionVec className="absolute bottom-6 left-0 w-full text-[#1769AA] opacity-[.12]" />
        <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <SectionHead eyebrow="Our workflow" title={["Five steps.", "No guesswork."]} />
            <Reveal delay={0.1}><MaskImage src="/img/surveyor.jpg" alt="Surveyor operating a total station on a tripod in the field" className="mt-10 aspect-[4/3] w-full" /></Reveal>
          </div>
          <div>
            <ol className="border-t border-[#102A43]/12">
              {STEPS.map((s, i) => (
                <Reveal as="li" key={s} delay={i * 0.07} className="group flex items-baseline gap-6 border-b border-[#102A43]/12 py-7">
                  <span className="font-mono text-sm text-[#1769AA]">0{i + 1}</span>
                  <span className="font-display text-2xl md:text-3xl font-bold text-[#102A43] transition-transform duration-300 group-hover:translate-x-2">{s}</span>
                </Reveal>
              ))}
            </ol>
            <Reveal delay={0.2}><Btn to="/get-started" className="mt-10" testid="workflow-start-project-button">Start Your Project</Btn></Reveal>
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="relative bg-[#F3F6F8] grid-light py-24 md:py-32" data-testid="pricing-preview-section">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <SectionHead eyebrow="Pricing" title={["Flexible pricing for", "survey drafting projects."]}
              intro="Pricing depends on survey type, project complexity, drawing volume, input data quality, output requirements and deadlines. Every engagement is quoted after we review your requirements." />
            <Reveal delay={0.15}><Btn to="/pricing" className="mt-10" testid="view-pricing-options-button">View Pricing Options</Btn></Reveal>
          </div>
          <ul className="self-end border-t border-[#102A43]/15">
            {PRICING.map(([t, code], i) => (
              <Reveal as="li" key={t} delay={i * 0.07}>
                <Link to={`/pricing#model-${code.toLowerCase()}`} data-testid={`pricing-preview-${code.toLowerCase()}`} className="group flex items-center justify-between gap-4 border-b border-[#102A43]/15 py-6">
                  <span className="flex items-baseline gap-5"><span className="font-mono text-xs text-[#1769AA]">MODEL {code}</span><span className="font-display text-xl md:text-2xl font-bold text-[#102A43] group-hover:text-[#1769AA] transition-colors">{t}</span></span>
                  <ArrowUpRight className="h-5 w-5 text-[#102A43] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#1769AA]" />
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <FinalCta />
    </>
  );
}
